import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listMessages,
  getMessageThread,
  listRecipients,
  sendMessage,
  markMessageRead,
  markAllMessagesRead,
  archiveMessage,
  type UserMessage,
} from "../api/messages";
import { getMyPermissions } from "../api/users";
import { useAuth } from "../auth/AuthContext";

type FolderType = "inbox" | "unread" | "sent" | "all";

function priorityBadge(priority: string) {
  switch (priority) {
    case "urgent":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-red-100 text-red-700 border border-red-200 animate-pulse">
          <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
          Urgent
        </span>
      );
    case "important":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200">
          Important
        </span>
      );
    default:
      return null;
  }
}

function categoryBadge(category: string) {
  switch (category) {
    case "playbook":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
          Sales Playbook
        </span>
      );
    case "performance":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          Campaign Audit
        </span>
      );
    case "billing":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
          Billing
        </span>
      );
    case "system":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">
          System Update
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
          Message
        </span>
      );
  }
}

function renderInlineFormatting(text: string) {
  // Split on **bold** and `code` segments safely
  const tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return tokens.map((tok, idx) => {
    if (tok.startsWith("**") && tok.endsWith("**")) {
      return (
        <strong key={idx} className="font-semibold text-gray-900">
          {tok.slice(2, -2)}
        </strong>
      );
    }
    if (tok.startsWith("`") && tok.endsWith("`")) {
      return (
        <code
          key={idx}
          className="px-1.5 py-0.5 mx-0.5 rounded bg-slate-100 text-slate-800 font-mono text-xs border border-slate-200"
        >
          {tok.slice(1, -1)}
        </code>
      );
    }
    return <span key={idx}>{tok}</span>;
  });
}

function ScriptCalloutCard({ lines }: { lines: string[] }) {
  const [copied, setCopied] = useState(false);
  const rawText = lines
    .map((l) => l.replace(/^>\s?/, "").replace(/^\*+|\*+$/g, "").replace(/^"|"$|^“|”$/g, ""))
    .join("\n")
    .trim();

  function handleCopy() {
    navigator.clipboard.writeText(rawText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="my-4 rounded-xl bg-gradient-to-r from-indigo-50/90 via-blue-50/70 to-white border-l-4 border-indigo-600 border border-indigo-100 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-indigo-600" />
          Word-for-Word Agent Talk Track
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="px-2.5 py-1 rounded-md text-xs font-medium bg-white hover:bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-2xs transition"
        >
          {copied ? "✓ Copied Script" : "Copy Script"}
        </button>
      </div>
      <div className="space-y-2 text-gray-800 text-[15px] leading-relaxed">
        {lines.map((line, idx) => {
          const cleaned = line.replace(/^>\s?/, "");
          if (!cleaned.trim()) return <div key={idx} className="h-1" />;
          return (
            <p key={idx} className="italic text-slate-800">
              {renderInlineFormatting(cleaned)}
            </p>
          );
        })}
      </div>
    </div>
  );
}

function RichMessageBody({ body }: { body: string }) {
  const lines = body.split("\n");
  const blocks: JSX.Element[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i++;
      continue;
    }

    if (/^---+$/.test(trimmed)) {
      blocks.push(<hr key={`hr-${i}`} className="my-6 border-gray-200" />);
      i++;
      continue;
    }

    // Blockquote / Script card
    if (trimmed.startsWith(">")) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        quoteLines.push(lines[i].trim());
        i++;
      }
      blocks.push(<ScriptCalloutCard key={`q-${i}`} lines={quoteLines} />);
      continue;
    }

    // Markdown Table
    if (trimmed.startsWith("|") && i + 1 < lines.length && lines[i + 1].trim().startsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i].trim());
        i++;
      }
      const parseRow = (r: string) =>
        r
          .replace(/^\||\|$/g, "")
          .split("|")
          .map((c) => c.trim());
      const headers = parseRow(tableLines[0]);
      const dataRows = tableLines
        .slice(1)
        .filter((r) => !/^\|?[\s\-:|]+\|?$/.test(r))
        .map(parseRow);

      blocks.push(
        <div key={`tbl-${i}`} className="my-4 overflow-x-auto rounded-lg border border-gray-200 shadow-2xs">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs font-semibold border-b border-gray-200">
              <tr>
                {headers.map((h, hi) => (
                  <th key={hi} className="px-3.5 py-2.5">
                    {renderInlineFormatting(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {dataRows.map((row, ri) => (
                <tr key={ri} className="hover:bg-slate-50/70">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3.5 py-2 text-gray-700">
                      {renderInlineFormatting(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Headings
    if (trimmed.startsWith("# ")) {
      blocks.push(
        <h1 key={`h1-${i}`} className="text-xl font-bold text-gray-900 mt-6 mb-3 pb-2 border-b border-gray-200">
          {renderInlineFormatting(trimmed.slice(2))}
        </h1>,
      );
      i++;
      continue;
    }
    if (trimmed.startsWith("## ")) {
      blocks.push(
        <h2
          key={`h2-${i}`}
          className="text-lg font-bold text-slate-900 mt-6 mb-2.5 flex items-center gap-2 bg-slate-100/80 px-3 py-2 rounded-lg border border-slate-200/80"
        >
          {renderInlineFormatting(trimmed.slice(3))}
        </h2>,
      );
      i++;
      continue;
    }
    if (trimmed.startsWith("### ")) {
      blocks.push(
        <h3 key={`h3-${i}`} className="text-base font-bold text-indigo-950 mt-5 mb-2">
          {renderInlineFormatting(trimmed.slice(4))}
        </h3>,
      );
      i++;
      continue;
    }
    if (trimmed.startsWith("#### ")) {
      blocks.push(
        <h4
          key={`h4-${i}`}
          className="text-sm font-bold text-indigo-900 mt-4 mb-1.5 uppercase tracking-wide bg-indigo-50/60 px-2.5 py-1 rounded border-l-3 border-indigo-500"
        >
          {renderInlineFormatting(trimmed.slice(5))}
        </h4>,
      );
      i++;
      continue;
    }

    // Bullet list
    if (/^(\*|-)\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^(\*|-)\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^(\*|-)\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={`ul-${i}`} className="my-2.5 space-y-1.5 pl-4">
          {items.map((it, idx) => (
            <li key={idx} className="text-sm text-gray-700 leading-relaxed flex items-start gap-2">
              <span className="text-indigo-600 font-bold mt-0.5">•</span>
              <span className="flex-1">{renderInlineFormatting(it)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: { num: string; text: string }[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        const m = lines[i].trim().match(/^(\d+)\.\s+(.*)$/);
        if (m) items.push({ num: m[1], text: m[2] });
        i++;
      }
      blocks.push(
        <ol key={`ol-${i}`} className="my-3 space-y-2 pl-1">
          {items.map((it, idx) => (
            <li key={idx} className="text-sm text-gray-700 leading-relaxed flex items-start gap-2.5">
              <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                {it.num}
              </span>
              <span className="flex-1">{renderInlineFormatting(it.text)}</span>
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    // Standard paragraph
    blocks.push(
      <p key={`p-${i}`} className="text-sm text-gray-700 leading-relaxed my-2">
        {renderInlineFormatting(trimmed)}
      </p>,
    );
    i++;
  }

  return <div className="space-y-1">{blocks}</div>;
}

export function MessagesPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: me } = useQuery({ queryKey: ["me-permissions"], queryFn: getMyPermissions });
  const isAdmin = me?.is_admin ?? false;

  const [folder, setFolder] = useState<FolderType>("inbox");
  const [usrFilter, setUsrFilter] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [composing, setComposing] = useState(false);

  // Compose state
  const [recipientId, setRecipientId] = useState<string>("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState<"normal" | "important" | "urgent">("important");
  const [category, setCategory] = useState<"general" | "playbook" | "performance" | "billing" | "system">("general");

  // Reply state
  const [replyBody, setReplyBody] = useState("");

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["messages", folder, usrFilter],
    queryFn: () => listMessages(folder, usrFilter),
    refetchInterval: 10_000,
  });

  const { data: recipients = [] } = useQuery({
    queryKey: ["message-recipients"],
    queryFn: listRecipients,
  });

  const selectedMessage: UserMessage | null =
    messages.find((m) => m.message_id === selectedId) ?? messages[0] ?? null;

  const { data: threadMessages = [] } = useQuery({
    queryKey: ["message-thread", selectedMessage?.thread_id],
    queryFn: () => getMessageThread(selectedMessage!.thread_id),
    enabled: !!selectedMessage?.thread_id,
    refetchInterval: 10_000,
  });

  const markReadMut = useMutation({
    mutationFn: (id: number) => markMessageRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages"] });
      qc.invalidateQueries({ queryKey: ["messages-unread-summary"] });
    },
  });

  const markAllReadMut = useMutation({
    mutationFn: () => markAllMessagesRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages"] });
      qc.invalidateQueries({ queryKey: ["messages-unread-summary"] });
    },
  });

  const archiveMut = useMutation({
    mutationFn: (id: number) => archiveMessage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages"] });
      qc.invalidateQueries({ queryKey: ["messages-unread-summary"] });
      setSelectedId(null);
    },
  });

  const sendMut = useMutation({
    mutationFn: sendMessage,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages"] });
      qc.invalidateQueries({ queryKey: ["message-thread"] });
      qc.invalidateQueries({ queryKey: ["messages-unread-summary"] });
      setComposing(false);
      setSubject("");
      setBody("");
      setReplyBody("");
    },
  });

  // Automatically mark selected unread inbox message as read after 1.5s of viewing
  useEffect(() => {
    if (!selectedMessage || selectedMessage.is_read) return;
    const isRecipient =
      selectedMessage.recipient_usr_id === user?.user_id ||
      (isAdmin && selectedMessage.recipient_usr_id === 1 && selectedMessage.sender_usr_id !== user?.user_id) ||
      !isAdmin;
    if (!isRecipient) return;
    const timer = setTimeout(() => {
      markReadMut.mutate(selectedMessage.message_id);
    }, 1200);
    return () => clearTimeout(timer);
  }, [selectedMessage?.message_id, selectedMessage?.is_read]);

  const unreadCountInList = messages.filter((m) => !m.is_read).length;

  function handleSendNew(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    if (isAdmin && recipientId === "ALL") {
      sendMut.mutate({
        broadcast_all: true,
        subject,
        body,
        priority,
        category,
      });
    } else {
      const targetId = isAdmin ? Number(recipientId) : recipients[0]?.usr_id ?? 1;
      sendMut.mutate({
        recipient_usr_id: targetId,
        subject,
        body,
        priority,
        category,
      });
    }
  }

  function handleSendReply(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedMessage || !replyBody.trim()) return;
    const otherUsrId =
      selectedMessage.sender_usr_id === user?.user_id
        ? selectedMessage.recipient_usr_id
        : selectedMessage.sender_usr_id;
    const replySubject = selectedMessage.subject.startsWith("Re:")
      ? selectedMessage.subject
      : `Re: ${selectedMessage.subject}`;
    sendMut.mutate({
      recipient_usr_id: otherUsrId,
      thread_id: selectedMessage.thread_id,
      subject: replySubject,
      body: replyBody,
      priority: "normal",
      category: selectedMessage.category,
    });
  }

  return (
    <div className="space-y-4">
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-xl shadow-xs border border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold text-lg shadow-xs">
            ✉
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              Dialer Inbox & Playbooks
              {unreadCountInList > 0 && (
                <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-red-600 text-white animate-pulse">
                  {unreadCountInList} Unread
                </span>
              )}
            </h1>
            <p className="text-xs text-gray-500">
              Direct messages, agent closing playbooks, campaign audits, and support threads
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {unreadCountInList > 0 && (
            <button
              type="button"
              onClick={() => markAllReadMut.mutate()}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 text-xs font-medium text-gray-700"
            >
              ✓ Mark all read
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (isAdmin && !recipientId && recipients.length > 0) {
                const jamalOrFirst = recipients.find((r) => r.username === "jamal") ?? recipients[0];
                setRecipientId(String(jamalOrFirst.usr_id));
              }
              setComposing(true);
            }}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-xs flex items-center gap-1.5"
          >
            <span>+</span> {isAdmin ? "Push Message to User" : "Message Support / Admin"}
          </button>
        </div>
      </div>

      {/* Folder Tabs + Admin User Filter */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg bg-white p-1 border border-gray-200 shadow-2xs">
          {(
            [
              { id: "inbox", label: "Inbox" },
              { id: "unread", label: "Unread Only" },
              { id: "sent", label: "Sent" },
              ...(isAdmin ? [{ id: "all", label: "All User Threads (Admin)" }] : []),
            ] as { id: FolderType; label: string }[]
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setFolder(tab.id);
                setSelectedId(null);
              }}
              className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition ${
                folder === tab.id
                  ? "bg-indigo-600 text-white shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500">Filter by Account:</label>
            <select
              value={usrFilter ?? ""}
              onChange={(e) => setUsrFilter(e.target.value ? Number(e.target.value) : null)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
            >
              <option value="">All Accounts</option>
              {recipients.map((r) => (
                <option key={r.usr_id} value={r.usr_id}>
                  {r.display_name} (@{r.username})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Main Split-Pane Email Box */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* Left Pane: Message List */}
        <div className="lg:col-span-4 bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-gray-200 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              {folder === "sent"
                ? "Sent Messages"
                : folder === "all"
                ? "All Platform Messages"
                : "Messages"}
            </span>
            <span className="text-xs text-gray-400">{messages.length} total</span>
          </div>

          {isLoading ? (
            <div className="p-8 text-center text-sm text-gray-400">Loading inbox…</div>
          ) : messages.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-2xl mb-1">📭</div>
              <div className="text-sm font-medium text-gray-700">No messages in this folder</div>
              <div className="text-xs text-gray-400 mt-0.5">
                New playbooks, alerts, and replies will appear here automatically.
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[72vh] overflow-y-auto">
              {messages.map((m) => {
                const isSelected = selectedMessage?.message_id === m.message_id;
                return (
                  <button
                    key={m.message_id}
                    type="button"
                    onClick={() => setSelectedId(m.message_id)}
                    className={`w-full text-left p-3.5 transition block border-l-4 ${
                      isSelected
                        ? "border-indigo-600 bg-indigo-50/50"
                        : !m.is_read
                        ? "border-red-500 bg-amber-50/30 hover:bg-amber-50/60"
                        : "border-transparent hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {!m.is_read && (
                          <span
                            className="w-2.5 h-2.5 rounded-full bg-red-600 shrink-0 animate-ping"
                            style={{ animationDuration: "1.8s" }}
                          />
                        )}
                        {!m.is_read && (
                          <span className="w-2.5 h-2.5 rounded-full bg-red-600 shrink-0 -ml-4" />
                        )}
                        <span
                          className={`text-xs truncate ${
                            !m.is_read ? "font-bold text-gray-900" : "font-medium text-gray-600"
                          }`}
                        >
                          {folder === "sent"
                            ? `To: ${m.recipient_name} (@${m.recipient_username})`
                            : folder === "all"
                            ? `${m.sender_name} → ${m.recipient_name}`
                            : m.sender_name}
                        </span>
                      </div>
                      <span className="text-[11px] text-gray-400 shrink-0">{m.created_at}</span>
                    </div>

                    <div
                      className={`text-sm mb-1.5 line-clamp-2 ${
                        !m.is_read ? "font-bold text-gray-950" : "font-semibold text-gray-800"
                      }`}
                    >
                      {m.subject}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {categoryBadge(m.category)}
                        {priorityBadge(m.priority)}
                      </div>
                      {m.thread_count > 1 && (
                        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          {m.thread_count} msgs
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Pane: Full Message & Thread Reader */}
        <div className="lg:col-span-8 bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
          {!selectedMessage ? (
            <div className="p-12 text-center text-gray-400">
              Select a message from the left to read it.
            </div>
          ) : (
            <div>
              {/* Message Header */}
              <div className="p-6 border-b border-gray-200 bg-gradient-to-b from-slate-50/80 to-white">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    {categoryBadge(selectedMessage.category)}
                    {priorityBadge(selectedMessage.priority)}
                    {!selectedMessage.is_read && (
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-red-600 text-white">
                        NEW
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!selectedMessage.is_read && (
                      <button
                        type="button"
                        onClick={() => markReadMut.mutate(selectedMessage.message_id)}
                        className="px-2.5 py-1 rounded border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50"
                      >
                        Mark Read
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => window.print()}
                      className="px-2.5 py-1 rounded border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50"
                    >
                      🖨 Print / PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => archiveMut.mutate(selectedMessage.message_id)}
                      className="px-2.5 py-1 rounded border border-gray-200 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Archive
                    </button>
                  </div>
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-2">{selectedMessage.subject}</h2>

                <div className="flex flex-wrap items-center justify-between text-xs text-gray-500 gap-2">
                  <div>
                    <span className="font-semibold text-gray-700">From:</span> {selectedMessage.sender_name} (
                    <span className="text-gray-400">@{selectedMessage.sender_username}</span>)
                    <span className="mx-2 text-gray-300">|</span>
                    <span className="font-semibold text-gray-700">To:</span> {selectedMessage.recipient_name} (
                    <span className="text-gray-400">@{selectedMessage.recipient_username}</span>)
                  </div>
                  <div>{selectedMessage.created_at}</div>
                </div>
              </div>

              {/* Thread Messages */}
              <div className="p-6 space-y-6 max-h-[65vh] overflow-y-auto">
                {(threadMessages.length > 0 ? threadMessages : [selectedMessage]).map((tm, idx) => (
                  <div
                    key={tm.message_id}
                    className={
                      idx > 0
                        ? "pt-6 border-t border-gray-200"
                        : ""
                    }
                  >
                    {threadMessages.length > 1 && (
                      <div className="flex items-center justify-between text-xs text-gray-400 mb-3 bg-gray-50 px-3 py-1.5 rounded">
                        <span className="font-semibold text-gray-700">
                          {tm.sender_name} (@{tm.sender_username})
                        </span>
                        <span>{tm.created_at}</span>
                      </div>
                    )}
                    <RichMessageBody body={tm.body} />
                  </div>
                ))}
              </div>

              {/* Reply Box */}
              <form onSubmit={handleSendReply} className="p-4 bg-slate-50 border-t border-gray-200">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Reply to {selectedMessage.sender_usr_id === user?.user_id ? selectedMessage.recipient_name : selectedMessage.sender_name}:
                </label>
                <textarea
                  rows={3}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Write a reply or question…"
                  className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm focus:border-indigo-500 focus:outline-hidden"
                />
                <div className="flex justify-end mt-2">
                  <button
                    type="submit"
                    disabled={sendMut.isPending || !replyBody.trim()}
                    className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold shadow-2xs"
                  >
                    {sendMut.isPending ? "Sending…" : "Send Reply"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Compose Modal */}
      {composing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-2xl overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">
                {isAdmin ? "Push Message / Playbook to Dialer User" : "Send Message to Support & Coaching Team"}
              </h3>
              <button
                type="button"
                onClick={() => setComposing(false)}
                className="text-gray-400 hover:text-gray-700 text-lg font-bold"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSendNew} className="p-6 space-y-4">
              {isAdmin && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-1">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Recipient</label>
                    <select
                      value={recipientId}
                      onChange={(e) => setRecipientId(e.target.value)}
                      required
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="ALL">📢 Broadcast to ALL Active Users</option>
                      {recipients.map((r) => (
                        <option key={r.usr_id} value={r.usr_id}>
                          {r.display_name} (@{r.username})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Category</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value as typeof category)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="playbook">Sales Playbook</option>
                      <option value="performance">Campaign Audit</option>
                      <option value="general">General Message</option>
                      <option value="billing">Billing</option>
                      <option value="system">System Update</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Priority (Flashes Badge)</label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as typeof priority)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="urgent">🚨 Urgent (Flashing Alert)</option>
                      <option value="important">⚡ Important</option>
                      <option value="normal">Normal</option>
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  required
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Loan-to-Debt Consolidation Closing Playbook & Call Audit"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Message Body (Supports Markdown headings `#`, bold `**`, tables, and `&gt;` talk-track cards)
                </label>
                <textarea
                  rows={10}
                  required
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write your message or paste a formatted playbook here…"
                  className="w-full rounded-lg border border-gray-300 p-3 text-sm font-mono"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setComposing(false)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendMut.isPending}
                  className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-xs"
                >
                  {sendMut.isPending ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
